import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { FieldTree, FormField, FormRoot } from '@angular/forms/signals';
import { MatError } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FileDropComponent } from '@app/shared/components/files/file-drop/file-drop.component';
import { FilesMaxSizeError, isFilesMaxSizeError } from '@app/shared/forms/rules';
import { ProjectTaskDTO } from '@app/shared/services/dtos/project-tasks/project-task.dto';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { Location } from '@app/shared/types/models/location/location.model';
import { ProjectIndustry } from '@app/shared/types/models/project-industry/project-industry.model';
import { ProjectParticipant } from '@app/shared/types/models/project/project-participant';
import { Project } from '@app/shared/types/models/project/project.model';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTiptapEditorComponent } from '@app/shared/ui/tiptap-editor/ui-tiptap-editor.component';
import { UiTreeSelectComponent } from '@app/shared/ui/tree-select/ui-tree-select.component';
import { proffeoBuildTree } from '@app/shared/ui/types/tree.types';
import { TranslatePipe } from '@ngx-translate/core';

export interface TaskCreateData {
  task: Partial<ProjectTaskDTO>;
  files: File[] | undefined;
}

/** Plain model of the create-task form (owned by the dialog, edited by `proffeo-task-create`). */
export interface TaskCreateFormModel {
  taskTypeId: string;
  locationId: string | null;
  industryId: string | null;
  name: string;
  description: string;
  files: File[];
}

export const TASK_CREATE_TITLE_MAX_LENGTH = 100;

/** Empty values stay absent from the request, exactly like the untouched reactive controls before. */
export function toTaskCreateData(value: TaskCreateFormModel): TaskCreateData {
  return {
    files: value.files,
    task: {
      taskTypeId: value.taskTypeId || undefined,
      locationId: value.locationId || undefined,
      industryId: value.industryId || undefined,
      name: value.name || undefined,
      description: value.description || undefined
    }
  };
}

@Component({
  selector: 'proffeo-task-create',
  templateUrl: './task-create.component.html',
  imports: [
    TranslatePipe,
    FormField,
    FormRoot,
    FileDropComponent,
    MatIconModule,
    MatError,
    UiSelectComponent,
    UiTreeSelectComponent,
    UiTiptapEditorComponent,
    UiInputComponent
  ]
})
export class TaskCreateComponent implements OnDestroy {
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly locationsService: LocationsDataService = inject(LocationsDataService);
  /** Object URLs of the image previews, created once per file and revoked when the file leaves the list. */
  private readonly blobUrls = new Map<File, string>();

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly taskParticipants: WritableSignal<ProjectParticipant[]> = signal([]);
  protected readonly allProjectLocations: Signal<Location[]> = computed(() => {
    const roots = this.projectLocations().filter(location => !location.readOnly);
    return roots.flatMap(location =>
      location?.id ? [location, ...this.locationsService.getAllSubLocations(location.id)] : [location]
    );
  });
  protected readonly locationNodes = computed(() =>
    proffeoBuildTree(this.allProjectLocations(), {
      getId: location => location.id ?? '',
      getParentId: location => location.parentLocation?.()?.id,
      getLabel: location => location.name ?? '',
      getValue: location => location.id ?? ''
    })
  );
  protected readonly industrySelectOptions = computed(() =>
    this.projectIndustries().map(projectIndustry => ({
      value: projectIndustry.industry().id,
      label: projectIndustry.industry().name
    }))
  );
  protected readonly files: Signal<File[]> = computed(() => this.createTaskForm().files().value());
  protected readonly filesSizeError: Signal<FilesMaxSizeError | undefined> = computed(() => {
    for (const error of this.createTaskForm().files().errors()) {
      if (isFilesMaxSizeError(error)) {
        return error;
      }
    }
    return undefined;
  });
  /** Image previews: an object URL is created once per file; URLs of removed files are revoked. */
  protected readonly previewUrls: Signal<ReadonlyMap<File, string>> = computed(() => {
    const images = this.files().filter(file => this.isImage(file));
    for (const [file, url] of this.blobUrls) {
      if (!images.includes(file)) {
        URL.revokeObjectURL(url);
        this.blobUrls.delete(file);
      }
    }
    for (const file of images) {
      if (!this.blobUrls.has(file)) {
        this.blobUrls.set(file, URL.createObjectURL(file));
      }
    }
    return new Map(this.blobUrls);
  });

  public readonly isCompact: InputSignal<boolean> = input<boolean>(false);
  public readonly parentTask: InputSignal<Task | undefined> = input<Task | undefined>(undefined);
  public readonly taskTypes: InputSignal<TaskType[]> = input<TaskType[]>([]);
  public readonly project: InputSignal<Project> = input.required<Project>();
  public readonly projectIndustries: InputSignal<ProjectIndustry[]> = input<ProjectIndustry[]>([]);
  public readonly projectParticipants: InputSignal<ProjectParticipant[]> = input<ProjectParticipant[]>([]);
  public readonly projectLocations: InputSignal<Location[]> = input<Location[]>([]);
  public readonly createTaskForm: InputSignal<FieldTree<TaskCreateFormModel>> =
    input.required<FieldTree<TaskCreateFormModel>>();
  public readonly firstStep: ModelSignal<boolean> = model(true);

  public ngOnDestroy(): void {
    this.blobUrls.forEach(blobUrl => URL.revokeObjectURL(blobUrl));
    this.blobUrls.clear();
  }

  protected selectTaskType(taskTypeId: string): void {
    this.createTaskForm().taskTypeId().value.set(taskTypeId);
    this.firstStep.set(false);
  }

  protected onRemoveFile(file: File): void {
    this.createTaskForm()
      .files()
      .value.update(files => files.filter((value: File) => value !== file));
  }

  protected onFilesAdded(files: File[]): void {
    this.createTaskForm()
      .files()
      .value.update(current => [...current, ...files]);
  }

  public isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }
}
